import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu, X, Calculator, Phone } from 'lucide-react';
import { Button } from './ui/button';
import QuickContactModal from './QuickContactModal';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showQuickContact, setShowQuickContact] = useState(false);

  const scrollToCalculator = () => {
    const calculator = document.getElementById('cost-calculator');
    if (calculator) {
      calculator.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // If not on home page, navigate to home with hash
      window.location.href = '/#cost-calculator';
    }
  };

  const jurisdictions = [
    { name: 'Dubai Mainland Company', link: '/mainland' },
    { name: 'Free Zone Setup', link: '/freezone' },
    { name: 'Offshore Company', link: '/offshore' },
    { name: 'Saudi Arabia (KSA)', link: '#ksa' },
  ];

  const services = [
    { name: 'Business Setup', link: '/#business-setup' },
    { name: 'Trade License', link: '/#license' },
    { name: 'Corporate Tax & VAT', link: '/#tax' },
    { name: 'Visa Services', link: '/#visa' },
    { name: 'Banking Assistance', link: '/#banking' },
  ];

  const indiaUaeItems = [
    { name: 'India-UAE Programs', link: '/india-uae-programs', highlight: true },
    { name: 'CEPA Trade Benefits', link: '/india-uae-trade' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-[#6B1B4D] to-[#2D1B3D] text-white py-2 px-4">
        <div className="container mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href="tel:+971528662076" className="flex items-center gap-2 hover:text-gray-200 transition">
              <Phone size={16} />
              <span>+971 52 866 2076</span>
            </a>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              className="border-white text-white hover:bg-white hover:text-[#6B1B4D] transition-all"
              onClick={() => setShowQuickContact(true)}
              data-testid="header-get-started-btn"
            >
              GET STARTED
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="border-white text-white hover:bg-white hover:text-[#6B1B4D] transition-all flex items-center gap-2"
              onClick={scrollToCalculator}
              data-testid="header-cost-calculator-btn"
            >
              <Calculator size={16} />
              COST CALCULATOR
            </Button>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-[#1a1033] text-white shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <div>
                <div className="text-3xl font-bold">
                  <span className="text-white">firmzy</span>
                  <span className="text-[#DC143C] text-xs ml-1">Chennai</span>
                </div>
                <p className="text-xs text-gray-300 mt-0.5">UAE Expansion, Hassle-Free</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/about" className="hover:text-[#DC143C] transition-colors">About Us</Link>
              
              <div 
                className="relative"
                onMouseEnter={() => setActiveDropdown('jurisdictions')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button className="flex items-center gap-1 hover:text-[#DC143C] transition-colors py-2">
                  Jurisdictions <ChevronDown size={16} />
                </button>
                {activeDropdown === 'jurisdictions' && (
                  <div className="absolute top-full left-0 pt-2 z-50">
                    <div className="w-64 bg-white text-gray-800 rounded-lg shadow-xl py-2">
                      {jurisdictions.map((item, idx) => (
                        <Link 
                          key={idx} 
                          to={item.link} 
                          className="block px-4 py-3 hover:bg-gray-100 transition"
                          onClick={() => setActiveDropdown(null)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div 
                className="relative"
                onMouseEnter={() => setActiveDropdown('services')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button className="flex items-center gap-1 hover:text-[#DC143C] transition-colors py-2">
                  Services <ChevronDown size={16} />
                </button>
                {activeDropdown === 'services' && (
                  <div className="absolute top-full left-0 pt-2 z-50">
                    <div className="w-56 bg-white text-gray-800 rounded-lg shadow-xl py-2">
                      {services.map((item, idx) => (
                        <Link 
                          key={idx} 
                          to={item.link} 
                          className="block px-4 py-3 hover:bg-gray-100 transition"
                          onClick={() => setActiveDropdown(null)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div 
                className="relative"
                onMouseEnter={() => setActiveDropdown('indiauae')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button className="flex items-center gap-1 font-medium text-[#FFD700] hover:text-[#DC143C] transition-colors py-2">
                  India-UAE <ChevronDown size={16} />
                </button>
                {activeDropdown === 'indiauae' && (
                  <div className="absolute top-full left-0 pt-2 z-50">
                    <div className="w-56 bg-white text-gray-800 rounded-lg shadow-xl py-2">
                      {indiaUaeItems.map((item, idx) => (
                        <Link 
                          key={idx} 
                          to={item.link} 
                          className={`block px-4 py-3 hover:bg-gray-100 transition ${item.highlight ? 'font-semibold text-[#DC143C]' : ''}`}
                          onClick={() => setActiveDropdown(null)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Link to="/#promotions" className="hover:text-[#DC143C] transition-colors">Promotions</Link>
              <Link to="/#blog" className="hover:text-[#DC143C] transition-colors">Blog</Link>
              <Link to="/#guides" className="hover:text-[#DC143C] transition-colors">Resources</Link>
              <Link to="/contact" className="hover:text-[#DC143C] transition-colors">Contact Us</Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden pb-4 animate-in slide-in-from-top">
              <Link to="/about" className="block py-2 hover:text-[#DC143C]">About Us</Link>
              <Link to="/mainland" className="block py-2 hover:text-[#DC143C]">Mainland</Link>
              <Link to="/freezone" className="block py-2 hover:text-[#DC143C]">Free Zone</Link>
              <Link to="/offshore" className="block py-2 hover:text-[#DC143C]">Offshore</Link>
              <div className="py-2 border-t border-white/20 mt-2">
                <p className="text-[#FFD700] font-medium mb-1">India-UAE</p>
                <Link to="/india-uae-programs" className="block py-1 pl-4 text-sm hover:text-[#DC143C]">Programs</Link>
                <Link to="/india-uae-trade" className="block py-1 pl-4 text-sm hover:text-[#DC143C]">Trade Benefits</Link>
              </div>
              <Link to="/#blog" className="block py-2 hover:text-[#DC143C]">Blog</Link>
              <Link to="/#guides" className="block py-2 hover:text-[#DC143C]">Resources</Link>
              <Link to="/contact" className="block py-2 hover:text-[#DC143C]">Contact Us</Link>
            </div>
          )}
        </div>
      </nav>
      
      {/* Quick Contact Modal */}
      <QuickContactModal 
        isOpen={showQuickContact} 
        onClose={() => setShowQuickContact(false)} 
      />
    </header>
  );
};

export default Header;